import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import UsersManagement from "./components/UsersManagement";
import DatabaseSecurityTab from "./components/DatabaseSecurityTab";

type Tab = "general" | "notifications" | "security" | "appearance" | "users" | "database";

export default function AdminSettingsPage() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";

  const [activeTab, setActiveTab] = useState<Tab>("general");
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  const [general, setGeneral] = useState({
    orgName: "BHODEV NGO",
    tagline: "Bogi Health Organization for Development",
    email: "info@bhodevcameroon.org",
    phone: "+237 677 000 000",
    address: "Buea, Southwest Region, Cameroon",
    website: "https://bhodevcameroon.org",
    facebook: "https://facebook.com/bhodevcameroon",
    twitter: "",
    linkedin: "",
  });

  const [notifications, setNotifications] = useState({
    newApplication: true,
    articlePublished: true,
    projectUpdate: false,
    weeklyReport: true,
    systemAlerts: true,
  });

  const [security, setSecurity] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    twoFactor: false,
    sessionTimeout: "30",
  });

  const [appearance, setAppearance] = useState({
    language: "English",
    dateFormat: "MMM DD, YYYY",
    timezone: "Africa/Douala",
  });

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from("settings").select("*").eq("id", 1).maybeSingle();
    if (error) {
      console.error("Error fetching settings:", error);
    } else if (data) {
      setGeneral({
        orgName: data.org_name || "BHODEV NGO",
        tagline: data.tagline || "",
        email: data.email || "",
        phone: data.phone || "",
        address: data.address || "",
        website: data.website || "",
        facebook: data.facebook || "",
        twitter: data.twitter || "",
        linkedin: data.linkedin || "",
      });
      setNotifications({
        newApplication: data.new_application ?? true,
        articlePublished: data.article_published ?? true,
        projectUpdate: data.project_update ?? false,
        weeklyReport: data.weekly_report ?? true,
        systemAlerts: data.system_alerts ?? true,
      });
      setAppearance({
        language: data.language || "English",
        dateFormat: data.date_format || "MMM DD, YYYY",
        timezone: data.timezone || "Africa/Douala",
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    const { error } = await supabase
      .from("settings")
      .update({
        org_name: general.orgName,
        tagline: general.tagline,
        email: general.email,
        phone: general.phone,
        address: general.address,
        website: general.website,
        facebook: general.facebook,
        twitter: general.twitter,
        linkedin: general.linkedin,
        new_application: notifications.newApplication,
        article_published: notifications.articlePublished,
        project_update: notifications.projectUpdate,
        weekly_report: notifications.weeklyReport,
        system_alerts: notifications.systemAlerts,
        language: appearance.language,
        date_format: appearance.dateFormat,
        timezone: appearance.timezone,
        updated_at: new Date().toISOString(),
      })
      .eq("id", 1);

    if (error) {
      console.error("Error saving settings:", error);
      return;
    }

    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const tabs: { id: Tab; label: string; icon: string; superadminOnly?: boolean }[] = [
    { id: "general", label: "General", icon: "ri-settings-3-line" },
    { id: "notifications", label: "Notifications", icon: "ri-notification-3-line" },
    { id: "security", label: "Security", icon: "ri-shield-keyhole-line" },
    { id: "appearance", label: "Appearance", icon: "ri-palette-line" },
    { id: "users", label: "Users", icon: "ri-team-line", superadminOnly: true },
    { id: "database", label: "DB Security", icon: "ri-database-2-line", superadminOnly: true },
  ];

  return (
    <AdminLayout title="Settings" subtitle="Configure your admin panel preferences">
      {loading && (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-primary text-2xl"></i>
        </div>
      )}
      {!loading && (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar tabs */}
          <div className="w-full lg:w-56 shrink-0">
            <div className="bg-white rounded-2xl border border-stone-100 p-2">
              {tabs
              .filter((t) => !t.superadminOnly || isSuperadmin)
              .map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer ${
                    activeTab === t.id ? "bg-primary text-white" : "text-stone-600 hover:bg-stone-100"
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`${t.icon} text-base`}></i>
                  </div>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1">
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              {/* General */}
              {activeTab === "general" && (
                <div>
                  <div className="px-6 py-5 border-b border-stone-100">
                    <h2 className="text-sm font-bold text-stone-800">Organization Information</h2>
                    <p className="text-xs text-stone-400 mt-0.5">Basic details about your organization</p>
                  </div>
                  <div className="px-6 py-5 flex flex-col gap-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Organization Name</label>
                        <input type="text" value={general.orgName} onChange={(e) => setGeneral({ ...general, orgName: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800" />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Tagline</label>
                        <input type="text" value={general.tagline} onChange={(e) => setGeneral({ ...general, tagline: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800" />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Contact Email</label>
                        <input type="email" value={general.email} onChange={(e) => setGeneral({ ...general, email: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800" />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Phone Number</label>
                        <input type="tel" value={general.phone} onChange={(e) => setGeneral({ ...general, phone: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800" />
                      </div>
                      <div className="col-span-full flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Address</label>
                        <input type="text" value={general.address} onChange={(e) => setGeneral({ ...general, address: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800" />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Website URL</label>
                        <input type="url" value={general.website} onChange={(e) => setGeneral({ ...general, website: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800" />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Facebook</label>
                        <input type="url" value={general.facebook} onChange={(e) => setGeneral({ ...general, facebook: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800" />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Twitter / X</label>
                        <input type="url" value={general.twitter} onChange={(e) => setGeneral({ ...general, twitter: e.target.value })} placeholder="https://twitter.com/..." className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400" />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">LinkedIn</label>
                        <input type="url" value={general.linkedin} onChange={(e) => setGeneral({ ...general, linkedin: e.target.value })} placeholder="https://linkedin.com/..." className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications */}
              {activeTab === "notifications" && (
                <div>
                  <div className="px-6 py-5 border-b border-stone-100">
                    <h2 className="text-sm font-bold text-stone-800">Notification Preferences</h2>
                    <p className="text-xs text-stone-400 mt-0.5">Choose what you want to be notified about</p>
                  </div>
                  <div className="px-6 py-5 flex flex-col gap-4">
                    {[
                      { key: "newApplication", label: "New Applications", desc: "Get notified when a new intern or volunteer applies" },
                      { key: "articlePublished", label: "Article Published", desc: "Receive a notification when an article goes live" },
                      { key: "projectUpdate", label: "Project Updates", desc: "Notifications for project status changes" },
                      { key: "weeklyReport", label: "Weekly Summary Report", desc: "Receive a weekly digest of all activity" },
                      { key: "systemAlerts", label: "System Alerts", desc: "Important system and security notifications" },
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                        <div>
                          <p className="text-sm font-semibold text-stone-800">{item.label}</p>
                          <p className="text-xs text-stone-400 mt-0.5">{item.desc}</p>
                        </div>
                        <button
                          onClick={() => setNotifications({ ...notifications, [item.key]: !notifications[item.key as keyof typeof notifications] })}
                          className={`relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer shrink-0 ${
                            notifications[item.key as keyof typeof notifications] ? "bg-primary" : "bg-stone-300"
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all duration-300 ${
                              notifications[item.key as keyof typeof notifications] ? "left-5.5 translate-x-0.5" : "left-0.5"
                            }`}
                          ></span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Security */}
              {activeTab === "security" && (
                <div>
                  <div className="px-6 py-5 border-b border-stone-100">
                    <h2 className="text-sm font-bold text-stone-800">Security Settings</h2>
                    <p className="text-xs text-stone-400 mt-0.5">Manage your password and account security</p>
                  </div>
                  <div className="px-6 py-5 flex flex-col gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Current Password</label>
                      <input type="password" value={security.currentPassword} onChange={(e) => setSecurity({ ...security, currentPassword: e.target.value })} placeholder="Enter current password" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">New Password</label>
                        <input type="password" value={security.newPassword} onChange={(e) => setSecurity({ ...security, newPassword: e.target.value })} placeholder="New password" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400" />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Confirm Password</label>
                        <input type="password" value={security.confirmPassword} onChange={(e) => setSecurity({ ...security, confirmPassword: e.target.value })} placeholder="Confirm new password" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                      <div>
                        <p className="text-sm font-semibold text-stone-800">Two-Factor Authentication</p>
                        <p className="text-xs text-stone-400 mt-0.5">Add an extra layer of security to your account</p>
                      </div>
                      <button
                        onClick={() => setSecurity({ ...security, twoFactor: !security.twoFactor })}
                        className={`relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer shrink-0 ${security.twoFactor ? "bg-primary" : "bg-stone-300"}`}
                      >
                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all duration-300 ${security.twoFactor ? "left-5.5 translate-x-0.5" : "left-0.5"}`}></span>
                      </button>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Session Timeout (minutes)</label>
                      <select value={security.sessionTimeout} onChange={(e) => setSecurity({ ...security, sessionTimeout: e.target.value })} className="w-full sm:w-48 px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-700 cursor-pointer">
                        <option value="15">15 minutes</option>
                        <option value="30">30 minutes</option>
                        <option value="60">1 hour</option>
                        <option value="120">2 hours</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Appearance */}
              {activeTab === "appearance" && (
                <div>
                  <div className="px-6 py-5 border-b border-stone-100">
                    <h2 className="text-sm font-bold text-stone-800">Appearance &amp; Localization</h2>
                    <p className="text-xs text-stone-400 mt-0.5">Customize how the admin panel looks and behaves</p>
                  </div>
                  <div className="px-6 py-5 flex flex-col gap-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Language</label>
                        <select value={appearance.language} onChange={(e) => setAppearance({ ...appearance, language: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-700 cursor-pointer">
                          <option>English</option>
                          <option>French</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Timezone</label>
                        <select value={appearance.timezone} onChange={(e) => setAppearance({ ...appearance, timezone: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-700 cursor-pointer">
                          <option value="Africa/Douala">Africa/Douala (WAT)</option>
                          <option value="UTC">UTC</option>
                          <option value="Europe/Paris">Europe/Paris (CET)</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Date Format</label>
                        <select value={appearance.dateFormat} onChange={(e) => setAppearance({ ...appearance, dateFormat: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-700 cursor-pointer">
                          <option value="MMM DD, YYYY">Apr 29, 2026</option>
                          <option value="DD/MM/YYYY">29/04/2026</option>
                          <option value="YYYY-MM-DD">2026-04-29</option>
                        </select>
                      </div>
                    </div>
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                          <i className="ri-information-line text-amber-600 text-sm"></i>
                        </div>
                        <p className="text-xs text-amber-700 leading-relaxed">
                          Some appearance settings may require a page refresh to take full effect.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Users Management — superadmin only */}
              {activeTab === "users" && isSuperadmin && (
                <UsersManagement currentUserId={user?.id || ""} />
              )}

              {/* Database Security — superadmin only */}
              {activeTab === "database" && isSuperadmin && (
                <DatabaseSecurityTab />
              )}

              {/* Save button */}
              {activeTab !== "users" && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-stone-100 bg-stone-50">
                  {saved && (
                    <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold">
                      <i className="ri-check-line"></i>
                      Settings saved successfully
                    </div>
                  )}
                  {!saved && <div></div>}
                  <button
                    onClick={handleSave}
                    className="flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-save-line"></i>
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}